import React from 'react';
import { useTranslation } from 'react-i18next';

type StatusKind = 'lead' | 'company' | 'deal' | 'payout' | 'user';

const STATUS_STYLES: Record<StatusKind, Record<string, string>> = {
  lead: {
    SUBMITTED: 'bg-amber-100 text-amber-800',
    ACCEPTED: 'bg-emerald-100 text-emerald-800',
    REJECTED: 'bg-rose-100 text-rose-800',
    DELETED: 'bg-slate-200 text-slate-700',
  },
  company: {
    AVAILABLE: 'bg-sky-100 text-sky-800',
    PENDING_CONTACT: 'bg-amber-100 text-amber-800',
    IN_NEGOTIATION: 'bg-indigo-100 text-indigo-800',
    DEAL_CLOSED: 'bg-emerald-100 text-emerald-800',
  },
  deal: {
    OPEN: 'bg-slate-100 text-slate-800',
    QUALIFIED: 'bg-sky-100 text-sky-800',
    OUTREACH_STARTED: 'bg-cyan-100 text-cyan-800',
    MEETING_SCHEDULED: 'bg-violet-100 text-violet-800',
    PROPOSAL_SENT: 'bg-fuchsia-100 text-fuchsia-800',
    IN_NEGOTIATION: 'bg-indigo-100 text-indigo-800',
    CLOSED_WON: 'bg-emerald-100 text-emerald-800',
    CLOSED_LOST: 'bg-rose-100 text-rose-800',
  },
  payout: {
    REQUESTED: 'bg-amber-100 text-amber-800',
    APPROVED: 'bg-sky-100 text-sky-800',
    REJECTED: 'bg-rose-100 text-rose-800',
    PROCESSED: 'bg-emerald-100 text-emerald-800',
  },
  user: {
    ACTIVE: 'bg-emerald-100 text-emerald-800',
    BLOCKED: 'bg-rose-100 text-rose-800',
  },
};

interface StatusBadgeProps {
  kind: StatusKind;
  status: string | null | undefined;
  labelKeyPrefix?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ kind, status, labelKeyPrefix }) => {
  const { t } = useTranslation();
  const safeStatus = status || 'UNKNOWN';
  const style = STATUS_STYLES[kind][safeStatus] || 'bg-slate-100 text-slate-700';
  const label = labelKeyPrefix
    ? t(`${labelKeyPrefix}.${safeStatus}`, {
        defaultValue: t(`status.${kind}.${safeStatus}`, safeStatus.replace(/_/g, ' ')),
      })
    : t(`status.${kind}.${safeStatus}`, safeStatus.replace(/_/g, ' '));

  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${style}`}>
      {label}
    </span>
  );
};

export default StatusBadge;
